import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { PageLoadingState } from '@/components/glitch-ui/empty-state';
import {
  useReportsQuery,
  reportTasksQueryOptions,
  businessQueryOptions,
  reportRunsQueryOptions,
} from '@/lib/query-options';
import { FileText } from 'lucide-react';
import { useAtomValue } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { organizationDataAtom } from '@/store';
import { useStrategyReport } from '@/components/business/strategy-report-panel';
import { landingReportId } from '@/lib/reports-landing';

export const Route = createFileRoute('/_authenticated/_layout/reports/')({
  component: ReportsIndexPage,
  staticData: {
    title: 'Reports',
    icon: FileText,
  },
});

function ReportsIndexPage() {
  const organizationData = useAtomValue(organizationDataAtom);
  const navigate = useNavigate();

  const { data: business, isLoading: businessLoading } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  const { data: reports = [], isLoading: reportsLoading } = useReportsQuery(
    organizationData?.id,
    1,
    20
  );

  const { data: taskStatus, isLoading: tasksLoading } = useQuery({
    ...reportTasksQueryOptions(business?.id ?? 0),
    enabled: !!business?.id,
  });

  const { data: activeRuns = [], isLoading: runsLoading } = useQuery(
    reportRunsQueryOptions(organizationData?.id ?? 0, true),
  );

  // The living Strategy Report is its own pipeline (#6707): the list draws
  // its row first, so the landing has to know whether that row exists.
  const {
    runsQuery,
    report: strategyReport,
    inFlight: strategyInFlight,
  } = useStrategyReport(organizationData?.id ?? 0, business?.id ?? 0);

  const isLoading = businessLoading || reportsLoading || tasksLoading || runsLoading || runsQuery.isLoading;

  useEffect(() => {
    // Wait for organization data to be loaded before navigating
    // This prevents a race condition where queries are disabled (isLoading=false)
    // but organization data hasn't been set yet, causing premature navigation to /reports/none
    if (!organizationData?.id) return;

    if (!isLoading) {
      navigate({
        to: '/reports/$reportId',
        params: {
          reportId: landingReportId({
            reportIds: reports.map((r) => r.id),
            hasInProgress: !!taskStatus?.has_in_progress || activeRuns.length > 0,
            hasStrategyRow: strategyReport !== null || strategyInFlight !== null,
          }),
        },
        replace: true,
      });
    }
  }, [isLoading, reports, taskStatus, activeRuns.length, strategyReport, strategyInFlight, navigate, organizationData?.id]);

  return <PageLoadingState />;
}
